import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Rating,
  Chip,
  TextField,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Autocomplete,
  Pagination
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { authApi, reviewApi } from '../services/api';
import { Review, UserReviewStats, ReviewRatings } from '../types';
import { useLocationSelector, usePostalCodeSearch } from '../hooks/useAddressData';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { GAEvents } from '../hooks/useGoogleAnalytics';

const categoryLabels = {
  childcare_education: '子育て・教育',
  medical_welfare: '医療・福祉',
  transportation: '交通・アクセス',
  shopping_commercial: '商業・買い物',
  living_environment: '住環境',
  government_services: '行政サービス'
};

const ReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [userStats, setUserStats] = useState<UserReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPublicView, setIsPublicView] = useState(false);
  
  // 認証状態
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  
  // アクセストークンの有効性をチェック
  const checkTokenValidity = () => {
    const accessToken = localStorage.getItem('accessToken');
    const zustandAuth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    
    console.log('🧩 LocalStorage Debug:');
    console.log('  - accessToken:', accessToken);
    console.log('  - zustand auth-storage:', zustandAuth);
    console.log('  - isAuthenticated from store:', isAuthenticated);
    
    if (isAuthenticated && !accessToken) {
      console.log('🧹 認証状態とlocalStorageが不整合、認証状態をクリア');
      clearAuth();
      return false;
    }
    return isAuthenticated && accessToken;
  };
  
  // 住所データ関連のフック
  const s3BaseUrl = process.env.REACT_APP_ADDRESS_DATA_BASE_URL;
  const {
    prefectures,
    selectedPrefecture,
    selectedCity,
    cities: availableCities,
    isLoading: isLoadingAddressData,
    error: addressDataError,
    setSelectedPrefectureCode,
    setSelectedCityCode,
    resetSelection,
    getLocationString
  } = useLocationSelector(s3BaseUrl);

  const {
    searchByPostalCode,
    isSearching: isSearchingPostalCode,
    searchError: postalCodeError
  } = usePostalCodeSearch();
  
  // 検索・フィルタ用の状態
  const [searchZipcode, setSearchZipcode] = useState('');
  const [filterAddress, setFilterAddress] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'rating_high' | 'rating_low'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage] = useState(10);

  // ページ読み込み時に口コミ一覧とユーザー統計を取得
  useEffect(() => {
    fetchReviewsAndStats();
  }, []);

  const fetchReviewsAndStats = async () => {
    setLoading(true);
    setError('');

    // より厳密な認証チェック
    const isReallyAuthenticated = checkTokenValidity();
    
    console.log('🔍 認証状態チェック:', isAuthenticated);
    console.log('🔍 localStorage accessToken:', localStorage.getItem('accessToken'));
    console.log('🔍 実際の認証状態:', isReallyAuthenticated);

    try {
      if (isReallyAuthenticated) {
        console.log('✅ 認証済みユーザー - 詳細APIを呼び出し');
        try {
          // 認証済みユーザー: 詳細な口コミ情報を取得
          const [reviewsResponse, statsResponse] = await Promise.all([
            reviewApi.getReviews(),
            reviewApi.getUserReviewStats()
          ]);
          
          setReviews(reviewsResponse.reviews || []);
          setUserStats(statsResponse);
          setIsPublicView(false);
        } catch (authError: any) {
          // 認証エラーの場合、パブリック表示にフォールバック
          console.warn('⚠️ 認証エラー、パブリック表示にフォールバック:', authError);
          const reviewsResponse = await reviewApi.getPublicReviews();
          setReviews(reviewsResponse.reviews || []);
          setUserStats(null);
          setIsPublicView(true);
        }
      } else {
        console.log('🚫 未認証ユーザー - パブリックAPIを呼び出し');
        // 未認証ユーザー: 制限付き口コミ情報を取得
        const reviewsResponse = await reviewApi.getPublicReviews();
        console.log('📦 パブリック口コミレスポンス:', reviewsResponse);
        setReviews(reviewsResponse.reviews || []);
        setUserStats(null);
        setIsPublicView(true);
      }
    } catch (err: any) {
      console.error('❌ データ取得エラー:', err);
      // 401エラー以外の場合のみエラー表示
      if (err.response?.status !== 401) {
        setError(err.response?.data?.error || 'データの取得でエラーが発生しました');
      }
    } finally {
      setLoading(false);
    }
  };

  // 郵便番号での検索実行
  const handleZipcodeSearch = async () => {
    if (!searchZipcode) {
      setError('郵便番号を入力してください');
      return;
    }

    const result = await searchByPostalCode(searchZipcode);
    if (result) {
      // Google Analytics: 郵便番号検索イベントを送信
      GAEvents.search(`zipcode:${searchZipcode}`);

      // 郵便番号検索が成功した場合の処理
      setFilterAddress(`${result.prefecture} ${result.city} ${result.address}`.trim());
      setCurrentPage(1);
      // 必要に応じて口コミを再取得
      await fetchReviewsAndStats();
    }
  };

  // 都道府県選択時
  const handlePrefectureChange = (prefectureCode: string) => {
    setSelectedPrefectureCode(prefectureCode);
    setCurrentPage(1); // ページをリセット
  };

  // 市区町村選択時
  const handleCityChange = (cityCode: string) => {
    setSelectedCityCode(cityCode);
    setCurrentPage(1); // ページをリセット
  };

  // 都道府県・市区町村での検索実行
  const handleLocationSearch = async () => {
    const locationString = getLocationString();
    if (!locationString) {
      setError('都道府県を選択してください');
      return;
    }

    // Google Analytics: 地域検索イベントを送信
    GAEvents.search(locationString);

    setFilterAddress(locationString);
    setCurrentPage(1);
    await fetchReviewsAndStats();
  };

  // フィルタ・検索条件のクリア
  const handleClearFilters = async () => {
    // Google Analytics: フィルタクリアイベントを送信
    GAEvents.filterApply('clear_all');

    setFilterAddress('');
    setSearchZipcode('');
    resetSelection();
    setCurrentPage(1);
    await fetchReviewsAndStats();
  };

  // ソート済みの口コミリストを計算
  const sortedReviews = useMemo(() => {
    const filtered = reviews.filter(review => {
      if (!filterAddress) return true;
      return review.address?.includes(filterAddress);
    });

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'rating_high':
          const avgA = calculateAverageRating(a.ratings);
          const avgB = calculateAverageRating(b.ratings);
          return avgB - avgA;
        case 'rating_low':
          const avgA2 = calculateAverageRating(a.ratings);
          const avgB2 = calculateAverageRating(b.ratings);
          return avgA2 - avgB2;
        default:
          return 0;
      }
    });
  }, [reviews, filterAddress, sortBy]);

  // ページネーション用の計算
  const totalPages = Math.ceil(sortedReviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const paginatedReviews = sortedReviews.slice(startIndex, startIndex + reviewsPerPage);

  // 平均評価を計算 (ReviewRatings型に対応)
  const calculateAverageRating = (ratings: ReviewRatings) => {
    const values = [
      ratings.childcare_education,
      ratings.medical_welfare,
      ratings.transportation,
      ratings.shopping_commercial,
      ratings.living_environment,
      ratings.government_services
    ];
    return values.reduce((sum, rating) => sum + rating, 0) / values.length;
  };

  // 日付フォーマット
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
  };

  if (loading && reviews.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}> {/* モバイルでパディングを調整 */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' }, // モバイルで縦並び
        gap: { xs: 2, sm: 0 } // モバイルでギャップを追加
      }}>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' }, // モバイルでタイトルサイズを調整
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          口コミ一覧
        </Typography>
        {isAuthenticated && (
          <Button 
            variant="contained" 
            onClick={() => navigate('/create-review')}
            sx={{ 
              ml: { xs: 0, sm: 2 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            口コミを投稿する
          </Button>
        )}
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 住所データ読み込みエラー */}
      {addressDataError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          住所データの読み込みに問題があります: {addressDataError}
        </Alert>
      )}

      {/* 郵便番号エラー */}
      {postalCodeError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {postalCodeError}
        </Alert>
      )}

      {/* 未認証ユーザー向けのバナー */}
      {isPublicView && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>詳細を確認するにはログインと１件以上の口コミ投稿が必要です</AlertTitle>
          現在は星評価のみ表示されています。コメントの詳細を見るには{' '}
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => navigate('/login')}
            sx={{ 
              ml: 1,
              borderRadius: '12px',
              background: 'rgba(59, 130, 246, 0.1)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: '#3B82F6',
              '&:hover': {
                background: 'rgba(59, 130, 246, 0.2)',
                borderColor: '#3B82F6',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            ログイン
          </Button>
          {' '}して口コミを投稿してください。
        </Alert>
      )}

      {/* ユーザー統計情報 */}
      {userStats && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <PersonIcon sx={{ mr: 1 }} />
            あなたの投稿統計
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                総投稿数
              </Typography>
              <Typography variant="h6">
                {userStats.review_count}件
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                投稿地域数
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {userStats.locations.length}箇所
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                最新投稿日
              </Typography>
              <Typography variant="h6">
                {userStats.locations.length > 0 ? formatDate(userStats.locations[0].created_at) : 'なし'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* 検索・フィルタセクション */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}> {/* モバイルでパディングを調整 */}
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.25rem' } // モバイルでタイトルサイズを調整
          }}
        >
          <FilterListIcon sx={{ mr: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          検索・フィルタ
        </Typography>

        {/* 郵便番号検索 */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>郵便番号で検索</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="郵便番号"
                placeholder="1000001"
                value={searchZipcode}
                onChange={(e) => setSearchZipcode(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <Button
                variant="contained"
                onClick={handleZipcodeSearch}
                disabled={isSearchingPostalCode}
                startIcon={isSearchingPostalCode ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: 'rgba(148, 163, 184, 0.5)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                検索
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 都道府県・市区町村検索 */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>都道府県・市区町村で検索</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                  options={prefectures}
                  getOptionLabel={(option) => option.name}
                  value={selectedPrefecture}
                  onChange={(_, value) => handlePrefectureChange(value?.code || '')}
                  disabled={isLoadingAddressData}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="都道府県"
                      size="small"
                    />
                  )}
                />

                <Autocomplete
                  options={availableCities}
                  getOptionLabel={(option) => option.name}
                  value={selectedCity}
                  onChange={(_, value) => handleCityChange(value?.code || '')}
                  disabled={!selectedPrefecture || isLoadingAddressData}
                  sx={{ minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="市区町村"
                      size="small"
                    />
                  )}
                />

                <Button
                  variant="contained"
                  onClick={handleLocationSearch}
                  disabled={!selectedPrefecture}
                  startIcon={<SearchIcon />}
                  sx={{
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: 'rgba(148, 163, 184, 0.5)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  検索
                </Button>
              </Box>

              {/* 選択中の地域表示 */}
              {getLocationString() && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    選択中の地域:
                  </Typography>
                  <Chip
                    label={getLocationString()}
                    color="primary"
                    variant="outlined"
                    icon={<LocationOnIcon />}
                  />
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ソート・フィルタコントロール */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>並び順</InputLabel>
            <Select
              value={sortBy}
              label="並び順"
              onChange={(e) => {
                const newSortBy = e.target.value as any;
                setSortBy(newSortBy);
                
                // Google Analytics: ソート変更イベントを送信
                GAEvents.filterApply(`sort_${newSortBy}`);
              }}
            >
              <MenuItem value="newest">新着順</MenuItem>
              <MenuItem value="oldest">古い順</MenuItem>
              <MenuItem value="rating_high">評価の高い順</MenuItem>
              <MenuItem value="rating_low">評価の低い順</MenuItem>
            </Select>
          </FormControl>

          {(filterAddress || searchZipcode) && (
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              sx={{
                borderRadius: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#EF4444',
                '&:hover': {
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderColor: '#EF4444',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              フィルタをクリア
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={fetchReviewsAndStats}
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={loading}
            sx={{
              borderRadius: '12px',
              background: 'rgba(34, 197, 94, 0.1)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              color: '#22C55E',
              '&:hover': {
                background: 'rgba(34, 197, 94, 0.2)',
                borderColor: '#22C55E',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                background: 'rgba(148, 163, 184, 0.1)',
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#94A3B8',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            更新
          </Button>
        </Box>

        {/* 現在のフィルタ表示 */}
        {filterAddress && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              フィルタ中:
            </Typography>
            <Chip
              label={filterAddress}
              color="secondary"
              variant="outlined"
              onDelete={() => setFilterAddress('')}
            />
          </Box>
        )}
      </Paper>

      {/* 結果統計 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="body1">
          {sortedReviews.length}件の口コミが見つかりました
          {filterAddress && ` (「${filterAddress}」でフィルタ中)`}
        </Typography>
      </Paper>

      {/* 口コミリスト */}
      {paginatedReviews.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            口コミが見つかりませんでした
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            検索条件を変更してお試しください
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {paginatedReviews.map((review) => (
            <Card 
              key={review.review_id} 
              sx={{ 
                p: { xs: 1, sm: 2 },
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 10px 30px 0 rgba(31, 38, 135, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.4), transparent)',
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 20px 40px 0 rgba(31, 38, 135, 0.3)',
                  '&::before': {
                    background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.8), transparent)',
                  }
                },
                transition: 'all 0.3s ease-in-out',
              }}
            > {/* モバイルでパディングを調整 */}
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* ヘッダー部分 */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' }, // モバイルで縦並び
                  gap: { xs: 1, sm: 0 }
                }}>
                  <Avatar sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography 
                      variant="h6"
                      sx={{ 
                        fontSize: { xs: '1rem', sm: '1.25rem' } // モバイルでタイトルサイズを調整
                      }}
                    >
                      口コミ - {review.location_key}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      flexWrap: 'wrap',
                      justifyContent: { xs: 'center', sm: 'flex-start' } // モバイルで中央揃え
                    }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' } // モバイルでフォントサイズを調整
                        }}
                      >
                        <CalendarIcon sx={{ fontSize: { xs: 12, sm: 14 }, mr: 0.5 }} />
                        {formatDate(review.created_at)}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: { xs: 12, sm: 14 }, mr: 0.5 }} />
                        {review.address}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Rating
                      value={calculateAverageRating(review.ratings)}
                      readOnly
                      precision={0.1}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {calculateAverageRating(review.ratings).toFixed(1)}
                    </Typography>
                  </Box>
                </Box>

                {/* ユーザー情報 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    投稿者: {review.user.nickname}
                  </Typography>
                </Box>

                {/* 詳細評価 */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  詳細評価
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      子育て・教育:
                    </Typography>
                    <Rating value={review.ratings.childcare_education} readOnly size="small" />
                    <Typography variant="body2">
                      ({review.ratings.childcare_education})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      医療・福祉:
                    </Typography>
                    <Rating value={review.ratings.medical_welfare} readOnly size="small" />
                    <Typography variant="body2">
                      ({review.ratings.medical_welfare})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      交通・アクセス:
                    </Typography>
                    <Rating value={review.ratings.transportation} readOnly size="small" />
                    <Typography variant="body2">
                      ({review.ratings.transportation})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      商業・買い物:
                    </Typography>
                    <Rating value={review.ratings.shopping_commercial} readOnly size="small" />
                    <Typography variant="body2">
                      ({review.ratings.shopping_commercial})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      住環境:
                    </Typography>
                    <Rating value={review.ratings.living_environment} readOnly size="small" />
                    <Typography variant="body2">
                      ({review.ratings.living_environment})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      行政サービス:
                    </Typography>
                    <Rating value={review.ratings.government_services} readOnly size="small" />
                    <Typography variant="body2">
                      ({review.ratings.government_services})
                    </Typography>
                  </Box>
                </Box>

                {/* コメント */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  詳細コメント
                </Typography>
                {isPublicView ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    詳細を確認するにはログインと１件以上の口コミ投稿が必要です
                  </Alert>
                ) : (
                  <Stack spacing={1}>
                    {review.comments.childcare_education && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">子育て・教育:</Typography>
                        <Typography variant="body2">{review.comments.childcare_education}</Typography>
                      </Box>
                    )}
                    {review.comments.medical_welfare && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">医療・福祉:</Typography>
                        <Typography variant="body2">{review.comments.medical_welfare}</Typography>
                      </Box>
                    )}
                    {review.comments.transportation && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">交通・アクセス:</Typography>
                        <Typography variant="body2">{review.comments.transportation}</Typography>
                      </Box>
                    )}
                    {review.comments.shopping_commercial && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">商業・買い物:</Typography>
                        <Typography variant="body2">{review.comments.shopping_commercial}</Typography>
                      </Box>
                    )}
                    {review.comments.living_environment && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">住環境:</Typography>
                        <Typography variant="body2">{review.comments.living_environment}</Typography>
                      </Box>
                    )}
                    {review.comments.government_services && (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">行政サービス:</Typography>
                        <Typography variant="body2">{review.comments.government_services}</Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}

          {/* ページネーション */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Stack>
      )}
    </Container>
  );
};

export default ReviewsPage;
